import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a price with French locale (space as thousands separator)
 * @param price - The price to format
 * @param showCurrency - Whether to append the € symbol (default: true)
 * @returns Formatted price string (e.g., "10 060 €")
 */
export function formatPrice(price: number | undefined | null, showCurrency = true): string {
  if (price === undefined || price === null) return '-';
  
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
  
  return showCurrency ? `${formatted} €` : formatted;
}

/**
 * Optimizes a Supabase Storage image URL by requesting a resized version.
 * Uses Supabase Image Transformation API (render/image endpoint).
 * Falls through for non-Supabase URLs.
 */
export function optimizeImageUrl(url: string | undefined | null, width: number = 250, format: 'origin' | 'webp' = 'webp'): string | undefined {
  if (!url) return undefined;
  
  // Only transform Supabase Storage URLs
  const supabaseStoragePattern = /\/storage\/v1\/object\/public\//;
  if (!supabaseStoragePattern.test(url)) return url;
  
  const optimized = url.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );
  
  const separator = optimized.includes('?') ? '&' : '?';
  return `${optimized}${separator}width=${width}&resize=contain&format=${format}&quality=75`;
}

/**
 * Returns the original Supabase Storage URL from an optimized render URL.
 */
export function getOriginalImageUrl(url: string): string {
  return url
    .replace('/storage/v1/render/image/public/', '/storage/v1/object/public/')
    .replace(/[?&](width|resize|format|quality)=[^&]*/g, '')
    .replace(/\?$/, '');
}
